/**
 * Color Design Tokens
 *
 * Single source of truth for all color values used in the design system.
 * These tokens are imported by both MUI theme and Tailwind config.
 */

import { alpha } from '@gto/helpers/alpha'

import { playingCardShadows } from './shadows'

// Neutral grey — base for secondary, action, and component state tokens
// Figma token: _components/chip/defaultEnabledBorder (0.23), input/filled/hoverFill (0.15), etc.
export const neutralGrey = 'rgb(168, 168, 177)'

// Base color scales
export const electricGreen = {
  50: '#eafeec',
  100: '#cdfcd0',
  200: '#aafbb2',
  300: '#87f894',
  400: '#6ef27e',
  500: '#58ec68',
  600: '#4ddb5d',
  700: '#3fc650',
  800: '#35b445',
  900: '#1e9332',
  A800: '#304132',
  A600: '#4F6F52',
  A400: '#6D9E72',
  A200: '#8CCC92',
  A100: '#ccff90',
} as const

export const pink = {
  50: 'rgba(252, 228, 236, 1)',
  100: 'rgba(248, 187, 208, 1)',
  200: 'rgba(244, 143, 177, 1)',
  300: 'rgba(240, 98, 146, 1)',
  400: 'rgba(236, 64, 122, 1)',
  500: 'rgba(233, 30, 99, 1)',
  600: 'rgba(216, 27, 96, 1)',
  700: 'rgba(194, 24, 91, 1)',
  800: 'rgba(173, 20, 87, 1)',
  900: 'rgba(136, 14, 79, 1)',
  A100: 'rgba(255, 128, 171, 1)',
  A200: 'rgba(255, 64, 129, 1)',
  A400: 'rgba(245, 0, 87, 1)',
  A700: 'rgba(197, 17, 98, 1)',
} as const

export const lime = {
  50: '#F9FBE7',
  100: '#F0F4C3',
  200: '#E6EE9C',
  300: '#DCE775',
  400: '#D4E157',
  500: '#CDDC39',
  600: '#C0CA33',
  700: '#AFB42B',
  800: '#9E9D24',
  900: '#827717',
  A100: '#F4FF81',
  A200: '#EEFF41',
  A400: '#C6FF00',
  A700: '#AEEA00',
} as const

// Material color references (imported from MUI)
export const materialColors = {
  green: {
    300: '#81c784',
    400: '#66bb6a',
    700: '#388e3c',
  },
  grey: {
    100: '#f5f5f5',
    300: '#CFCFCF',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  lightBlue: {
    300: '#4fc3f7',
    400: '#29b6f6',
    700: '#0288d1',
  },
  orange: {
    300: '#ffb74d',
    400: '#ffa726',
    700: '#f57c00',
  },
  red: {
    300: '#e57373',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
  },
  yellow: {
    300: '#fff176',
  },
} as const

// Semantic colors
export const semanticColors = {
  primary: Object.freeze({
    main: electricGreen[200], // #aafbb2
    dark: '#67DE73',
    light: electricGreen[50], // #eafeec
    contrastText: 'rgba(0, 0, 0, 0.87)',
    statesColor: 'rgb(167, 167, 177)',
    states: {
      hover: alpha(electricGreen[200], 0.08), // rgba(170,251,178,0.08) — primary outlined chip hover bg
      focus: alpha(electricGreen[200], 0.12), // rgba(170,251,178,0.12) — primary outlined deleteBtn hover bg
    },
  }),
  secondary: Object.freeze({
    main: 'rgba(167, 167, 177, 0.1)',
    dark: 'rgba(167, 167, 177, 0.3)',
    light: 'rgb(213, 213, 223)',
    disabled: 'rgba(167, 167, 177, 0.05)',
    contrastText: 'rgba(0, 0, 0, 0.87)',
    statesColor: 'rgba(224, 224, 224)',
    states: {
      hover: alpha(neutralGrey, 0.15), // rgba(168,168,177,0.15) — default filled chip hover bg
      active: alpha(neutralGrey, 0.2), // rgba(168,168,177,0.2) — default filled chip active bg
      border: alpha(neutralGrey, 0.23), // rgba(168,168,177,0.23) — default outlined chip border
      activeBorder: '#616161', // default outlined chip active border
    },
  }),
  error: Object.freeze({
    main: materialColors.red[500], // #f44336
    dark: materialColors.red[700], // #d32f2f
    light: materialColors.red[300], // #e57373
    contrastText: 'rgba(0, 0, 0, 0.87)',
  }),
  warning: Object.freeze({
    main: materialColors.orange[400], // #ffa726
    dark: materialColors.orange[700], // #f57c00
    light: materialColors.orange[300], // #ffb74d
    contrastText: 'rgba(0, 0, 0, 0.87)',
  }),
  info: Object.freeze({
    main: materialColors.lightBlue[400], // #29b6f6
    dark: materialColors.lightBlue[700], // #0288d1
    light: materialColors.lightBlue[300], // #4fc3f7
    contrastText: 'rgba(0, 0, 0, 0.87)',
  }),
  success: Object.freeze({
    main: materialColors.green[400], // #66bb6a
    dark: materialColors.green[700], // #388e3c
    light: materialColors.green[300], // #81c784
    contrastText: 'rgba(0, 0, 0, 0.87)',
  }),
} as const

// Text colors
export const textColors = {
  primary: '#ffffff',
  secondary: 'rgba(255, 255, 255, 0.7)',
  disabled: 'rgba(255, 255, 255, 0.22)',
  ghost: 'rgba(255, 255, 255, 0.56)',
  hover: 'rgba(255, 255, 255, 0.08)',
  _states: {
    focus: 'rgba(255, 255, 255, 0.12)',
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(255, 255, 255, 0.16)',
  },
} as const

// Background colors
export const backgroundColors = {
  default: '#09090C',
  paper: '#09090C',
} as const

// Paper elevation colors (surface scale)
export const paperColors = {
  elevation0: '#09090C',
  elevation1: '#121214',
  elevation2: '#161618',
  elevation3: '#1B1B1D',
  elevation4: '#1E1E21',
  elevation5: '#26262A',
  elevation6: '#2B2B30',
  elevation7: '#303036',
  elevation8: '#35353B',
  elevation9: '#393940',
  elevation10: '#3D3D45',
  elevation11: '#41414A',
  elevation12: '#45454E',
  elevation13: '#494952',
  elevation14: '#4C4C56',
  elevation15: '#4F4F5A',
  elevation16: '#52525E',
  elevation17: '#555561',
  elevation18: '#585864',
  elevation19: '#5B5B67',
  elevation20: '#5E5E6A',
  elevation21: '#60606D',
  elevation22: '#626270',
  elevation23: '#626270',
  elevation24: '#626270',
} as const

// Compare-table row-tone gradient stops (subscription tier-overview). Sit between
// paper.elevation1/2 and elevation3/4 with no exact match on the elevation scale — named
// tokens here rather than raw rgb() literals in the component, per paperColors above.
export const compareTableToneColors = {
  light: { top: '#1C1C20', bottom: '#18181B' },
  default: { top: '#141417', bottom: '#111113' },
} as const

// Action colors
export const actionColors = {
  active: alpha(neutralGrey, 0.56),
  hover: alpha(neutralGrey, 0.08),
  selected: alpha(neutralGrey, 0.2),
  // MUI palette action defaults (mode-independent; previously read off the MUI theme via useTheme).
  hoverOpacity: 0.08,
  focusOpacity: 0.12,
  selectedOpacity: 0.2,
  activatedOpacity: 0.24,
  outlinedBorderOpacity: 0.5,
  disabled: 'rgba(167, 167, 177, 0.38)',
  disabledBackground: 'rgba(167, 167, 177, 0.22)',
} as const

// Domain-specific colors

export const diffIndicatorColors = {
  minPositive: '#832323',
  maxPositive: '#ea3a39',
  minNegative: '#125382',
  maxNegative: '#008AEE',
} as const

export const bettingColors = {
  call: '#5AB966',
  check: '#5AB966',
  fold: '#3D7CB8',
  range: '#FF8F00',
  raise: {
    default: '#F03C3C',
    smallMin: '#F03C3C',
    smallMax: '#DD3737',
    mediumMin: '#CA3232',
    mediumMax: '#B62E2E',
    largeMin: '#A32929',
    largeMax: '#902424',
    obMin: '#902424',
    obMax: '#6A1A1A',
  },
  player: {
    oop: '#97EAF8',
    ip: '#25B336',
  },
} as const

export const gradingColors = {
  best: electricGreen[200],
  correct: electricGreen[200],
  inaccurate: materialColors.yellow[300],
  mistake: materialColors.red[500],
  blunder: materialColors.red[500],
  noGrade: materialColors.lightBlue[400],
} as const

// Categorical palette for user-assignable labels/tags. Ordered — the first entry
// is the default selection. Decorative hues (not semantic), spaced around the hue
// wheel so tags stay distinguishable, and tuned for white text on the swatch.
export const tagColors = [
  '#F25C57',
  '#E97852',
  '#E5B466',
  '#EBCB44',
  '#B1D14D',
  '#569976',
  '#5ABEB9',
  '#8DCAC7',
  '#3761BE',
  '#6964EC',
  '#A05BCD',
  '#EF99EB',
] as const

export const colorDots = {
  pokerArena: '#24CB34',
  red: '#F35C57',
  orange: '#E97852',
  lightOrange: '#E5B466',
  yellow: '#EBCB44',
  lime: '#B1D14E',
  green: '#569977',
  teal: '#59BEB9',
  lightTeal: '#8DCAC7',
  blue: '#3761BE',
  indigo: '#6964EC',
  purple: '#A05BCD',
  pink: '#EF99EB',
} as const satisfies Record<string, string>

export const playingCardColors = {
  dropShadows: playingCardShadows,
  borders: {
    default: '#d1d5db',
  },
  fourColorDeck: {
    spades: '#121212',
    hearts: '#E72341',
    diamonds: '#1042C8',
    clubs: '#119B15',
  },
  twoColorDeck: {
    spades: '#121212',
    hearts: '#E72341',
    diamonds: '#E72341',
    clubs: '#121212',
  },
} as const

export const cardSuitColors = {
  spades: {
    primary: {
      dark: '#BDBDBD',
      light: '#504F4F',
    },
    hovered: '#BDBDBD',
    secondary: '#535356',
  },
  hearts: {
    primary: {
      dark: '#F05451',
      light: '#F64740',
    },
    hovered: '#FF5D56',
    secondary: '#B53B36',
  },
  diamonds: {
    primary: {
      dark: '#448BFF',
      light: '#3C91E6',
    },
    hovered: '#3C91E6',
    secondary: '#336FAA',
  },
  clubs: {
    primary: {
      dark: '#66BB6A',
      light: '#5AB966',
    },
    hovered: '#72C37C',
    secondary: '#488A50',
  },
  default: {
    primary: {
      dark: '#BBBBBB',
      light: '#BBBBBB',
    },
    hovered: '#FFFFFF',
    secondary: '#8C8C8C',
  },
} as const

export const tableColors = {
  header: '#9E9E9E',
  // Figma row-hover for the solution-selector data tables (#A7A7C7 @ 10%).
  // Exposed as the `bg-table-row-hover` Tailwind class via `table:` in the preset.
  'row-hover': alpha('rgb(167, 167, 199)', 0.1),
  // Subtle 1px row separator on stats/breakdown tables — `border-table-row-border`.
  'row-border': 'rgba(255, 255, 255, 0.04)',
} as const

// Trainer table colors
// !IMPORTANT: Would be extended later
export const trainerTableColors = {
  trainer_player_hero: semanticColors.primary.main,
  trainer_player_opponent: semanticColors.warning.main,
} as const

// Note colors — the design system's `palette/note` categorical accent set (note-1 … note-12 in
// Figma). Mirrored from the GTOW Design System library as consumers pull them in; note-6 exists
// in Figma but no code consumer has needed its value yet, so it is deliberately not guessed here.
export const noteColors = {
  1: '#F35C57',
  2: '#E97852',
  3: '#E5B466',
  4: '#EBCB44',
  5: '#B1D14E',
  7: '#59BEB9',
  8: '#8DCAC7',
  9: '#3761BE',
  10: '#6964EC',
  11: '#A05BCD',
  12: '#EF99EB',
} as const

// Additional utility colors

export const utilityColors = {
  cyan: {
    300: '#4dd0e1',
    400: '#26c6da',
    600: '#00ACC1',
    900: '#4DD0E1',
  },
  deepPurple: {
    200: '#b39ddb',
    400: '#7e57c2',
    600: '#5E35B1',
    900: '#311b92',
  },
  indigo: {
    A200: '#536DFE',
  },
  amber: {
    600: '#ffb300',
  },
  graphite: {
    200: '#323233',
  },
  table: {
    header: '#9E9E9E',
    rowBorder: tableColors['row-border'],
  },
  playerPosition: {
    placeholderBg: '#161618',
    placeholderActiveBg: '#29292D',
  },
  brands: {
    alipay: '#00ACEF',
    // Discord "blurple" + its hover shade, per the Discord brand palette.
    discord: '#5865F2',
    discordHover: '#4752C4',
  },
  divider: 'rgba(255, 255, 255, 0.12)',
} as const

export const pricingColors = {
  greenDim: alpha(electricGreen[200], 0.1),
  greenBorder: alpha(electricGreen[200], 0.25),
} as const

// Component-specific colors
export const componentColors = {
  input: {
    filled: {
      hoverFill: alpha(neutralGrey, 0.15),
      enabledFill: alpha(neutralGrey, 0.1),
    },
  },
  toggle: {
    group: {
      fill: alpha(neutralGrey, 0.1),
    },
  },
  switch: {
    slideFill: 'rgba(168, 168, 177, 0.38)',
    knobFillEnabled: materialColors.grey[300],
    knobFillDisabled: materialColors.grey[600],
  },
  checkbox: {
    glowPrimary: alpha(electricGreen[200], 0.5),
    glowDefault: '#58585E',
    glowSecondary: alpha(semanticColors.secondary.light, 0.5),
    glowError: alpha(semanticColors.error.main, 0.5),
    glowWarning: alpha(semanticColors.warning.main, 0.5),
    glowInfo: alpha(semanticColors.info.main, 0.5),
    glowSuccess: alpha(semanticColors.success.main, 0.5),
    borderDisabled: '#FFFFFF2E',
  },
} as const

// Button state opacity levels (consistent across all colors)
export const buttonStateOpacity = {
  glow: 0.5,
  outline: 0.5,
  focus: 0.3,
  selected: 0.16,
  hover: 0.1,
} as const

/**
 * Button color tokens - derived from base semantic colors with consistent opacity levels
 *
 * Each button color has:
 * - main: The primary button color (background for contained, text for outlined/text)
 * - contrastText: Text color on contained buttons
 * - statesBase: Base color for interaction states (often different from main)
 * - glow: 50% opacity for hover glow shadow
 * - outline: 50% opacity for outlined variant border
 * - focus: 30% opacity for focus background
 * - selected: 16% opacity for active/selected background
 * - hover: 10% opacity for text variant hover background
 */
export const buttonColors = {
  primary: {
    main: electricGreen[200], // #aafbb2
    contrastText: 'rgba(0, 0, 0, 0.87)',
    statesBase: 'rgb(167, 167, 177)', // Neutral states for primary
    glow: alpha(electricGreen[200], buttonStateOpacity.glow),
    outline: alpha('rgb(167, 167, 177)', buttonStateOpacity.outline),
    focus: alpha('rgb(167, 167, 177)', buttonStateOpacity.focus),
    selected: alpha('rgb(167, 167, 177)', buttonStateOpacity.selected),
    hover: alpha('rgb(167, 167, 177)', buttonStateOpacity.hover),
  },
  secondary: {
    main: 'rgba(167, 167, 177, 0.1)',
    contrastText: 'rgba(255, 255, 255, 0.7)',
    statesBase: 'rgb(224, 224, 224)',
    glow: 'none', // Secondary doesn't have glow
    outline: alpha('rgb(224, 224, 224)', buttonStateOpacity.outline),
    focus: alpha('rgb(224, 224, 224)', buttonStateOpacity.focus),
    selected: alpha('rgb(224, 224, 224)', buttonStateOpacity.selected),
    hover: alpha('rgb(224, 224, 224)', buttonStateOpacity.hover),
  },
  error: {
    main: materialColors.red[500], // #f44336
    contrastText: 'rgba(0, 0, 0, 0.87)',
    statesBase: materialColors.red[500],
    glow: alpha(materialColors.red[500], buttonStateOpacity.glow),
    outline: alpha(materialColors.red[500], buttonStateOpacity.outline),
    focus: alpha(materialColors.red[500], buttonStateOpacity.focus),
    selected: alpha(materialColors.red[500], buttonStateOpacity.selected),
    hover: alpha(materialColors.red[500], buttonStateOpacity.hover),
  },
  warning: {
    main: materialColors.orange[400], // #ffa726
    contrastText: 'rgba(0, 0, 0, 0.87)',
    statesBase: materialColors.orange[400],
    glow: alpha(materialColors.orange[400], buttonStateOpacity.glow),
    outline: alpha(materialColors.orange[400], buttonStateOpacity.outline),
    focus: alpha(materialColors.orange[400], buttonStateOpacity.focus),
    selected: alpha(materialColors.orange[400], buttonStateOpacity.selected),
    hover: alpha(materialColors.orange[400], buttonStateOpacity.hover),
  },
  info: {
    main: materialColors.lightBlue[400], // #29b6f6
    contrastText: 'rgba(0, 0, 0, 0.87)',
    statesBase: materialColors.lightBlue[400],
    glow: alpha(materialColors.lightBlue[400], buttonStateOpacity.glow),
    outline: alpha(materialColors.lightBlue[400], buttonStateOpacity.outline),
    focus: alpha(materialColors.lightBlue[400], buttonStateOpacity.focus),
    selected: alpha(materialColors.lightBlue[400], buttonStateOpacity.selected),
    hover: alpha(materialColors.lightBlue[400], buttonStateOpacity.hover),
  },
} as const

// Consolidated color export
export const colors = Object.freeze({
  // Base scales
  electricGreen: Object.freeze(electricGreen),
  pink: Object.freeze(pink),
  lime: Object.freeze(lime),

  // Material colors (for compatibility)
  ...materialColors,

  // Semantic
  ...semanticColors,

  // Text
  text: textColors,

  // Background
  background: backgroundColors,

  // Paper (surface scale)
  paper: paperColors,

  // Compare-table row-tone gradient stops
  compareTableTone: compareTableToneColors,

  // Action
  action: actionColors,

  // Domain-specific
  diffIndicator: diffIndicatorColors,
  note: noteColors,
  betting: bettingColors,
  grading: gradingColors,
  tags: tagColors,
  colorDots,
  playingCards: playingCardColors,
  cards: cardSuitColors,

  // Utility
  ...utilityColors,

  // Components
  _components: componentColors,

  // Button colors
  button: buttonColors,

  // Pricing overview
  pricing: pricingColors,
})
